import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Bell, Smartphone, Sparkles, CheckCircle, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Indtast venligst din email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Indtast venligst en gyldig email");
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.rpc('insert_email_signup', {
        signup_email: email.trim()
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };

      if (result.success) {
        setIsSuccess(true);
        toast.success("Du er nu tilmeldt! Vi sender dig besked når appen er klar.");
      } else if (result.error === 'Email already registered') {
        toast.info("Denne email er allerede tilmeldt waitlisten.");
        setIsSuccess(true);
      } else {
        toast.error(result.error || "Noget gik galt. Prøv igen.");
      }
    } catch (error) {
      console.error("Error signing up:", error);
      toast.error("Noget gik galt. Prøv igen senere.");
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 15
      }
    }
  } as const;

  const featureCardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 15
      }
    }
  } as const;

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut" as const
    }
  };

  return (
    <>
      <Helmet>
        <title>Waitlist - MySabbat App | Få besked når appen lanceres</title>
        <meta 
          name="description" 
          content="Tilmeld dig MySabbat waitlisten og få besked når appen er klar til download på App Store og Google Play." 
        />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          {/* Animated background decoration */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          />
          <motion.div 
            className="absolute top-20 left-10 w-72 h-72 bg-accent/20 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.2, 0.3, 0.2]
            }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.15, 1],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />

          <div className="container relative">
            <motion.div 
              className="max-w-2xl mx-auto text-center"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Badge */}
              <motion.div 
                variants={itemVariants}
                className="inline-flex items-center gap-2 bg-accent/15 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-8"
              >
                <motion.div animate={floatingAnimation}>
                  <Sparkles className="w-4 h-4 text-accent" />
                </motion.div>
                Kommer snart
              </motion.div>

              {/* Headline */}
              <motion.h1 
                variants={itemVariants}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
              >
                MySabbat App
                <br />
                <motion.span 
                  className="text-primary inline-block"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 100 }}
                >
                  på vej til dig
                </motion.span>
              </motion.h1>

              <motion.p 
                variants={itemVariants}
                className="text-lg md:text-xl text-muted-foreground mb-10 max-w-lg mx-auto"
              >
                Planlæg dit sabbatår direkte fra din telefon. Tilmeld dig waitlisten og vær blandt de første til at opleve appen.
              </motion.p>

              {/* Signup Form */}
              <motion.div variants={itemVariants}>
                <AnimatePresence mode="wait">
                  {!isSuccess ? (
                    <motion.form 
                      key="form"
                      onSubmit={handleSubmit} 
                      className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                    >
                      <Input
                        type="email"
                        placeholder="Din email adresse"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 h-12 rounded-full px-6 border-border bg-card"
                        disabled={isLoading}
                      />
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button 
                          type="submit"
                          size="lg"
                          disabled={isLoading}
                          className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full px-8 h-12 w-full sm:w-auto"
                        >
                          {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Bell className="w-4 h-4 mr-2" />
                              Giv mig besked
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </motion.form>
                  ) : (
                    <motion.div 
                      key="success"
                      className="flex items-center justify-center gap-3 bg-accent/15 text-accent-foreground px-6 py-4 rounded-2xl max-w-md mx-auto"
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                      >
                        <CheckCircle className="w-6 h-6" />
                      </motion.div>
                      <p className="font-medium">Du er på listen! Vi sender dig besked.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <motion.p 
                variants={itemVariants}
                className="text-sm text-muted-foreground mt-4"
              >
                Vi spammer ikke. Kun vigtige opdateringer om app-lanceringen.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Features Preview */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container">
            <motion.h2 
              className="text-2xl md:text-3xl font-bold text-center text-foreground mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
            >
              Hvad kan du forvente?
            </motion.h2>

            <motion.div 
              className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.15 }
                }
              }}
            >
              {[
                { icon: Smartphone, title: "Alt i én app", desc: "Planlæg, organiser og hold styr på dit sabbatår fra din lomme.", color: "accent" },
                { icon: Bell, title: "Smarte påmindelser", desc: "Få notifikationer om vigtige deadlines og muligheder.", color: "primary" },
                { icon: Sparkles, title: "Personlige forslag", desc: "Få skræddersyede anbefalinger baseret på dine interesser.", color: "accent" }
              ].map((feature, index) => (
                <motion.div 
                  key={index}
                  variants={featureCardVariants}
                  whileHover={{ 
                    y: -8, 
                    scale: 1.02,
                    transition: { type: "spring", stiffness: 300 }
                  }}
                  className="bg-card p-6 rounded-2xl border border-border text-center cursor-default"
                >
                  <motion.div 
                    className={`w-14 h-14 bg-${feature.color}/15 rounded-2xl flex items-center justify-center mx-auto mb-4`}
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <feature.icon className={`w-7 h-7 text-${feature.color}`} />
                  </motion.div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* App Store Badges Placeholder */}
        <motion.section 
          className="py-16 md:py-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="max-w-lg mx-auto text-center">
              <motion.p 
                className="text-muted-foreground mb-6"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                Snart tilgængelig på
              </motion.p>
              <motion.div 
                className="flex justify-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 0.5, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <motion.div 
                  className="bg-foreground text-background px-6 py-3 rounded-xl font-medium"
                  whileHover={{ scale: 1.05, opacity: 0.7 }}
                >
                  App Store
                </motion.div>
                <motion.div 
                  className="bg-foreground text-background px-6 py-3 rounded-xl font-medium"
                  whileHover={{ scale: 1.05, opacity: 0.7 }}
                >
                  Google Play
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.section>
      </main>

      <Footer />
    </>
  );
};

export default Waitlist;
